import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api, errMsg } from "../../lib/api";
import { POLDA_CATS } from "../../lib/icons";

const KELUAR = ["keluar_selasa", "keluar_rabu", "keluar_kamis", "keluar_jumat"];
const num = (v) => parseInt(v, 10) || 0;

const FieldInput = ({ f, value, onChange, form }) => {
  const id = `field-${f.key}`;
  if (f.type === "textarea") return <Textarea id={id} rows={3} value={value} onChange={(e) => onChange(e.target.value)} data-testid={id} />;
  if (f.type === "readonly") {
    const sisa = num(form.stock) - KELUAR.reduce((a, k) => a + num(form[k]), 0);
    return <Input id={id} value={sisa} disabled data-testid={id} />;
  }
  return (
    <Input id={id} type={f.type === "number" ? "number" : "text"} list={f.type === "kondisi" ? "kondisi-options" : undefined}
      value={value} onChange={(e) => onChange(e.target.value)} data-testid={id} />
  );
};

export const ItemFormDialog = ({ open, item, cat, meta, user, defaultPolda, onClose, onSaved }) => {
  const isEdit = !!item?.item_id;
  const [form, setForm] = useState({});
  const [polda, setPolda] = useState("");
  const [moveTo, setMoveTo] = useState(cat.key);
  const canMove = isEdit && POLDA_CATS.includes(cat.key);
  const [saving, setSaving] = useState(false);
  const needPolda = cat.polda_scoped && user.role !== "polda";

  useEffect(() => {
    if (open) {
      setForm({ ...(item?.data || {}) });
      setPolda(item?.polda || defaultPolda || "");
      setMoveTo(cat.key);
    }
  }, [open, item, defaultPolda, cat.key]);

  const submit = async (e) => {
    e.preventDefault();
    if (needPolda && !polda) return toast.error("Pilih Polda terlebih dahulu");
    setSaving(true);
    try {
      const body = { data: form, polda: needPolda ? polda : null, ...(canMove && moveTo !== cat.key ? { category: moveTo } : {}) };
      if (isEdit) await api.put(`/items/${cat.key}/${item.item_id}`, body);
      else await api.post(`/items/${cat.key}`, body);
      toast.success(isEdit ? "Data berhasil diperbarui" : "Data berhasil ditambahkan");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-sm" data-testid="item-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Ubah" : "Tambah"} Data {cat.label}</DialogTitle>
          <DialogDescription>
            {isEdit ? `Terakhir diperbarui oleh ${item.updated_by || "-"}` : "Lengkapi kolom yang tersedia. Kolom boleh dikosongkan bila tidak ada data."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          {canMove && (
            <div className="space-y-1.5">
              <Label>Kategori Almatsus</Label>
              <Select value={moveTo} onValueChange={setMoveTo}>
                <SelectTrigger data-testid="field-move-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {meta.categories.filter((c) => POLDA_CATS.includes(c.key)).map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {moveTo !== cat.key && <p className="text-xs text-amber-700">Data akan dipindahkan saat disimpan. Kolom yang tidak ada di kategori tujuan disalin ke Keterangan.</p>}
            </div>
          )}
          {needPolda && (
            <div className="space-y-1.5">
              <Label>Polda *</Label>
              <Select value={polda} onValueChange={setPolda}>
                <SelectTrigger data-testid="field-polda"><SelectValue placeholder="Pilih Polda" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {meta.polda_list.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            {cat.fields.map((f) => (
              <div key={f.key} className={`space-y-1.5 ${f.type === "textarea" ? "sm:col-span-2" : ""}`}>
                <Label htmlFor={`field-${f.key}`}>{f.label}</Label>
                <FieldInput f={f} form={form} value={form[f.key] || ""} onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))} />
              </div>
            ))}
          </div>
          <datalist id="kondisi-options">
            <option value="Baik" /><option value="Rusak Ringan" /><option value="Rusak Berat" />
          </datalist>
          <DialogFooter className="sticky -bottom-6 -mx-6 px-6 py-3 bg-card border-t gap-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="item-form-cancel" className="h-11 sm:h-9">Batal</Button>
            <Button type="submit" disabled={saving} data-testid="item-form-submit" className="h-11 sm:h-9">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Simpan
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
